import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Download, Trash2, PlayCircle, RefreshCw, Database, AlertTriangle, Loader2, CheckCircle2, FileArchive } from "lucide-react";
import { PageHeader } from "../components/ui/page-header";
import { EmptyState } from "../components/ui/empty-state";
import { Button } from "../components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

export default function BackupData() {
    const [items, setItems] = useState([]);
    const [logs, setLogs] = useState([]);
    const [retention, setRetention] = useState(30);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);
    const [confirmDel, setConfirmDel] = useState(null);
    const [confirmStage, setConfirmStage] = useState(0);

    const load = async () => {
        setLoading(true);
        try {
            const [l, lg] = await Promise.all([api.get("/backup/list"), api.get("/backup/logs")]);
            setItems(l.data.items || []);
            setRetention(l.data.retention_days || 30);
            setLogs(lg.data || []);
        } catch (e) { toast.error(e.response?.data?.detail || "Gagal memuat daftar backup"); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const triggerManual = async () => {
        setRunning(true);
        try {
            const r = await api.post("/backup/manual");
            if (r.data.status === "sukses") toast.success(`Backup berhasil — ${r.data.total_dokumen} dokumen, ${(r.data.ukuran_bytes / 1024 / 1024).toFixed(2)} MB`);
            else toast.error(`Backup gagal: ${r.data.error_msg || "unknown"}`);
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Gagal menjalankan backup"); }
        finally { setRunning(false); }
    };

    const download = async (tanggal) => {
        try {
            const token = localStorage.getItem("bvg_token");
            const r = await fetch(`${api.defaults.baseURL}/backup/${tanggal}/download`, { headers: { Authorization: `Bearer ${token}` } });
            if (!r.ok) throw new Error("fail");
            const blob = await r.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `backup_${tanggal}.zip`;
            document.body.appendChild(a); a.click(); a.remove();
            toast.success(`Download backup ${tanggal} dimulai`);
        } catch { toast.error("Gagal download backup"); }
    };

    const askDelete = (tanggal) => { setConfirmDel(tanggal); setConfirmStage(1); };
    const confirmDelete = async () => {
        if (!confirmDel) return;
        try {
            await api.delete(`/backup/${confirmDel}`);
            toast.success(`Backup ${confirmDel} dihapus`);
            setConfirmDel(null); setConfirmStage(0);
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Gagal hapus backup"); }
    };

    const latest = items[0];

    return (
        <div data-testid="backup-page">
            <PageHeader
                title="Backup Data"
                description={`Backup otomatis setiap hari pukul 23:30 WIB. Disimpan ${retention} hari terakhir.`}
                actions={
                    <div className="flex gap-2 flex-wrap">
                        <Button onClick={load} variant="outline" data-testid="backup-refresh" className="rounded-lg gap-1.5">
                            <RefreshCw className="w-4 h-4" /> Muat Ulang
                        </Button>
                        <Button onClick={triggerManual} disabled={running} data-testid="backup-manual-trigger" className="rounded-lg gap-1.5 text-white" style={{ background: "var(--apple-blue)" }}>
                            {running ? <><Loader2 className="w-4 h-4 animate-spin" /> Backup…</> : <><PlayCircle className="w-4 h-4" /> Backup Sekarang</>}
                        </Button>
                    </div>
                }
            />

            <div className="space-y-4 md:space-y-5 max-w-5xl">
                {/* Section Latest */}
                <section className="rounded-xl bg-white p-5" style={{ border: "1px solid var(--border)" }}>
                    <div className="flex items-start gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "#E8F7EE", color: "#1DA359" }}>
                            <Database className="w-5 h-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <h2 className="font-semibold text-base" style={{ color: "var(--text-primary)" }}>Backup Terbaru</h2>
                            <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>Snapshot terakhir berhasil disimpan.</p>
                        </div>
                    </div>
                    {loading ? (
                        <div className="text-sm py-4" style={{ color: "var(--text-muted)" }}>Memuat...</div>
                    ) : latest ? (
                        <div className="flex items-center justify-between gap-3 flex-wrap p-4 rounded-lg" style={{ background: "var(--bg-subtle)" }}>
                            <div className="flex items-center gap-3">
                                <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                                <div>
                                    <div className="font-mono font-semibold text-sm" style={{ color: "var(--text-primary)" }}>{latest.tanggal}</div>
                                    <div className="text-xs" style={{ color: "var(--text-muted)" }}>{latest.ukuran_mb} MB · {latest.jumlah_file} file</div>
                                </div>
                            </div>
                            <Button onClick={() => download(latest.tanggal)} variant="outline" className="rounded-lg gap-1.5" data-testid={`backup-download-${latest.tanggal}`}>
                                <Download className="w-4 h-4" /> Download Zip
                            </Button>
                        </div>
                    ) : (
                        <div className="text-sm py-4" style={{ color: "var(--text-muted)" }}>Belum ada backup. Klik "Backup Sekarang" untuk memulai.</div>
                    )}
                </section>

                {/* Section Daftar */}
                <section className="rounded-xl bg-white overflow-hidden" style={{ border: "1px solid var(--border)" }}>
                    <div className="px-5 py-4" style={{ borderBottom: "1px solid var(--border)" }}>
                        <h2 className="font-semibold text-base" style={{ color: "var(--text-primary)" }}>Daftar Backup</h2>
                        <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>Riwayat snapshot otomatis + manual.</p>
                    </div>
                    {loading ? (
                        <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--apple-blue)" }} /></div>
                    ) : items.length === 0 ? (
                        <EmptyState icon={FileArchive} title="Belum ada backup" description="Klik Backup Sekarang untuk memulai." testid="backup-empty" />
                    ) : (
                        <>
                            {/* Mobile card list */}
                            <div className="md:hidden p-3 space-y-2">
                                {items.map((it) => (
                                    <div key={it.tanggal} className="rounded-lg p-3" style={{ border: "1px solid var(--border)" }} data-testid={`backup-row-${it.tanggal}`}>
                                        <div className="flex items-center justify-between gap-2 mb-1.5">
                                            <div className="font-mono font-semibold text-sm" style={{ color: "var(--text-primary)" }}>{it.tanggal}</div>
                                            <div className="flex items-center gap-1">
                                                <button onClick={() => download(it.tanggal)} data-testid={`backup-download-${it.tanggal}`} className="p-2 rounded-lg hover:bg-[var(--bg-subtle)]" style={{ color: "var(--apple-blue)" }}><Download className="w-4 h-4" /></button>
                                                <button onClick={() => askDelete(it.tanggal)} data-testid={`backup-delete-${it.tanggal}`} className="p-2 rounded-lg hover:bg-[var(--apple-red-soft)]" style={{ color: "var(--apple-red)" }}><Trash2 className="w-4 h-4" /></button>
                                            </div>
                                        </div>
                                        <div className="text-xs num-tabular" style={{ color: "var(--text-muted)" }}>{it.ukuran_mb} MB · {it.jumlah_file} file</div>
                                    </div>
                                ))}
                            </div>
                            {/* Desktop table */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="w-full text-sm" data-testid="backup-table">
                                    <thead>
                                        <tr style={{ background: "var(--bg-subtle)", borderBottom: "1px solid var(--border)" }}>
                                            <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Tanggal</th>
                                            <th className="text-right px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Ukuran</th>
                                            <th className="text-right px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Jumlah File</th>
                                            <th className="text-right px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Aksi</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map((it) => (
                                            <tr key={it.tanggal} style={{ borderBottom: "1px solid var(--border-soft)" }} data-testid={`backup-row-${it.tanggal}`}>
                                                <td className="px-5 py-3 font-mono" style={{ color: "var(--text-primary)" }}>{it.tanggal}</td>
                                                <td className="px-5 py-3 text-right num-tabular" style={{ color: "var(--text-primary)" }}>{it.ukuran_mb} MB</td>
                                                <td className="px-5 py-3 text-right num-tabular" style={{ color: "var(--text-primary)" }}>{it.jumlah_file}</td>
                                                <td className="px-5 py-3 text-right">
                                                    <button onClick={() => download(it.tanggal)} data-testid={`backup-download-${it.tanggal}`} className="p-2 rounded-lg hover:bg-[var(--bg-subtle)] inline-flex" style={{ color: "var(--apple-blue)" }}><Download className="w-4 h-4" /></button>
                                                    <button onClick={() => askDelete(it.tanggal)} data-testid={`backup-delete-${it.tanggal}`} className="p-2 rounded-lg hover:bg-[var(--apple-red-soft)] inline-flex ml-1" style={{ color: "var(--apple-red)" }}><Trash2 className="w-4 h-4" /></button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </section>

                {/* Section Log */}
                <section className="rounded-xl bg-white overflow-hidden" style={{ border: "1px solid var(--border)" }}>
                    <div className="px-5 py-4" style={{ borderBottom: "1px solid var(--border)" }}>
                        <h2 className="font-semibold text-base" style={{ color: "var(--text-primary)" }}>Log Terakhir (10)</h2>
                        <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>Histori eksekusi backup (otomatis / manual).</p>
                    </div>
                    {logs.length === 0 ? (
                        <EmptyState title="Belum ada log" description="Log akan muncul setelah backup pertama berjalan." testid="backup-log-empty" />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr style={{ background: "var(--bg-subtle)", borderBottom: "1px solid var(--border)" }}>
                                        <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Waktu (UTC)</th>
                                        <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Trigger</th>
                                        <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Status</th>
                                        <th className="text-right px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Dokumen</th>
                                        <th className="text-right px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Ukuran</th>
                                        <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Catatan</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.map((l) => (
                                        <tr key={l.id} style={{ borderBottom: "1px solid var(--border-soft)" }}>
                                            <td className="px-5 py-3 font-mono text-xs" style={{ color: "var(--text-primary)" }}>{(l.waktu_mulai || "").replace("T", " ").slice(0, 19)}</td>
                                            <td className="px-5 py-3 text-xs" style={{ color: "var(--text-muted)" }}>{l.trigger}</td>
                                            <td className="px-5 py-3">
                                                <span className={`text-[10px] font-semibold uppercase px-2 py-1 rounded-md border ${l.status === "sukses" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : l.status === "gagal" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{l.status}</span>
                                            </td>
                                            <td className="px-5 py-3 text-right num-tabular" style={{ color: "var(--text-primary)" }}>{l.total_dokumen || 0}</td>
                                            <td className="px-5 py-3 text-right num-tabular" style={{ color: "var(--text-primary)" }}>{((l.ukuran_bytes || 0) / 1024 / 1024).toFixed(2)} MB</td>
                                            <td className="px-5 py-3 text-xs" style={{ color: "var(--apple-red)" }}>{l.error_msg || "-"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </div>

            {/* AlertDialog 2-stage confirm delete */}
            <AlertDialog open={!!confirmDel} onOpenChange={(o) => { if (!o) { setConfirmDel(null); setConfirmStage(0); } }}>
                <AlertDialogContent className="md:rounded-2xl" data-testid="backup-del-modal">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5" style={{ color: "var(--apple-red)" }} /> Hapus Backup
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {confirmStage === 1 ? (
                                <>Yakin ingin menghapus backup <b className="font-mono">{confirmDel}</b>? Backup yang terhapus tidak dapat dipulihkan.</>
                            ) : (
                                <span style={{ color: "var(--apple-red)" }} className="font-medium">KONFIRMASI AKHIR — tekan Hapus Permanen untuk benar-benar menghapus backup <b className="font-mono">{confirmDel}</b>.</span>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-lg">Batal</AlertDialogCancel>
                        {confirmStage === 1 ? (
                            <AlertDialogAction onClick={(e) => { e.preventDefault(); setConfirmStage(2); }} data-testid="backup-del-confirm1" className="rounded-lg text-white" style={{ background: "var(--apple-red)" }}>Lanjut</AlertDialogAction>
                        ) : (
                            <AlertDialogAction onClick={confirmDelete} data-testid="backup-del-confirm2" className="rounded-lg text-white" style={{ background: "var(--apple-red)" }}>Hapus Permanen</AlertDialogAction>
                        )}
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
